import type { Editor } from '@tiptap/vue-3'
import type { MaybeRefOrGetter, Ref } from 'vue'

import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'

import type { EditorContentMessageKeys } from '@/shared/lib'
import { computeCharacterCountError, EDITOR_CONTENT_LENGTH_DEFAULTS, mapEditorContentErrorMessage } from '@/shared/lib'

interface UseEditorContentValidationOptions {
  editor: Ref<Editor | undefined>
  maxLength?: MaybeRefOrGetter<number | undefined>
  messageKeys: EditorContentMessageKeys
  minLength?: MaybeRefOrGetter<number | undefined>
  required?: MaybeRefOrGetter<boolean | undefined>
  lazy?: boolean
}

export const useEditorContentValidation = ({
  editor,
  maxLength,
  messageKeys,
  minLength,
  required,
  lazy = true,
}: UseEditorContentValidationOptions) => {
  const { t } = useI18n()

  const resolvedMinLength = computed(() => toValue(minLength) ?? EDITOR_CONTENT_LENGTH_DEFAULTS.MIN)
  const resolvedMaxLength = computed(() => toValue(maxLength) ?? EDITOR_CONTENT_LENGTH_DEFAULTS.MAX)
  const resolvedRequired = computed(() => toValue(required) ?? false)

  const contentLength = computed(() => editor.value?.storage.characterCount.characters() ?? 0)

  const contentError = computed(() =>
    computeCharacterCountError(contentLength.value, {
      minLength: resolvedMinLength.value,
      maxLength: resolvedMaxLength.value,
      required: resolvedRequired.value,
    }),
  )

  const rules = {
    contentLength: {
      isValid: helpers.withMessage(messageKeys.tooShort, () => contentError.value === null),
    },
  }

  const v$ = useVuelidate(rules, { contentLength }, { $lazy: lazy, $scope: false })

  const touchContentValidation = () => {
    v$.value.contentLength.$touch()
  }

  const showErrorMessage = computed(() => v$.value.contentLength.$error)

  const contentErrorMessage = computed(() => {
    if (!showErrorMessage.value) {
      return ''
    }

    return mapEditorContentErrorMessage(contentError.value, t, messageKeys, {
      min: resolvedMinLength.value,
      max: resolvedMaxLength.value,
    })
  })

  const contentCounterLabel = computed(() => `${contentLength.value}/${resolvedMaxLength.value}`)

  const isCounterOverLimit = computed(() => contentError.value === 'too_long')

  const isInvalid = computed(() => v$.value.$invalid)

  const isContentValid = computed(() => contentError.value === null)

  const validate = async () => {
    return await v$.value.$validate()
  }

  const resetValidation = () => {
    v$.value.$reset()
  }

  return {
    contentCounterLabel,
    contentLength,
    contentError,
    contentErrorMessage,
    isCounterOverLimit,
    isContentValid,
    isInvalid,
    resetValidation,
    showErrorMessage,
    touchContentValidation,
    validate,
    v$,
  }
}
